/**
 * M14 Deep price analysis — hand-written API functions (Slice P6c).
 *
 * The deep-analysis endpoints (`POST /pricing/<seller_id>/deep-analysis/` and
 * `GET /pricing/<seller_id>/deep-analysis/<result_id>/`) are NOT in the generated
 * OpenAPI schema, so — exactly like the sibling estimate call (`priceEstimateApi.js`)
 * — these are hand-written against the shared axios client (`apiClient.request`).
 * The design (plan § "P6 un-held") asks the rows to be mapped to camelCase so the
 * compare panel reads `sourceLabel` / `priceTypical` / `comps[].sourceUrl` cleanly.
 * The mapper is kept LOCAL to this endpoint (a small field map, not a global
 * humps-style util) so it doesn't fight the rest of the codebase's snake_case
 * convention.
 *
 * Backend contract (P6b, fixed — do not change backend):
 *   POST /pricing/<seller_id>/deep-analysis/   body { part_type, ymm, condition }
 *     200 → a fresh cached row (status "succeeded").
 *     202 → a pending row (freshly queued or deduped onto an in-flight run).
 *     400 → a required field was missing (thrown as an axios error).
 *     403 body { detail, code: "upgrade_required" } → org not entitled (plan must
 *          be Shop/Pro + active). Resolved to `{ row: null, locked: true }` — NOT
 *          thrown — so the caller can render the upsell instead of an error.
 *   GET /pricing/<seller_id>/deep-analysis/<result_id>/?asking_price=<optional>
 *     200 → the row; 404 → unknown id (thrown as an axios error).
 *
 *   Row JSON (row-level Decimals are STRINGS; `comps[].price_usd` is a JSON number —
 *   the panel formats, it does not do maths):
 *     { id, status: "pending"|"succeeded"|"failed", part_type, ymm, condition,
 *       price_min, price_typical, price_max, comp_count, source_label, model_id,
 *       comps: [{ title, price_usd, source_url }], error, created_at, verdict }
 *     verdict ∈ "above"|"at"|"below" (asking-price placement vs the band) or null;
 *     only populated on succeeded + a valid asking_price.
 */


/** snake_case → camelCase for the fixed deep-analysis row. The row-level Decimal
 * fields (`price_min`/`price_typical`/`price_max`) are passed through as STRINGS
 * exactly as the backend sends them; `comps[].price_usd` arrives as a JSON number
 * (the comps live in a JSONField, so DRF passes it through untouched). The panel
 * formats, it does not do math (same rationale as `mapEstimate`). `null`/undefined → null. */
function mapDeepAnalysis(data) {
  if (!data) {
    return null;
  }
  return {
    id: data.id,
    status: data.status,
    partType: data.part_type,
    ymm: data.ymm,
    condition: data.condition,
    priceMin: data.price_min,
    priceTypical: data.price_typical,
    priceMax: data.price_max,
    compCount: data.comp_count,
    sourceLabel: data.source_label,
    modelId: data.model_id,
    comps: Array.isArray(data.comps)
      ? data.comps.map((comp) => ({
        title: comp.title,
        priceUsd: comp.price_usd,
        sourceUrl: comp.source_url,
      }))
      : [],
    error: data.error,
    createdAt: data.created_at,
    verdict: data.verdict,
  };
}


/**
 * Start (or reuse) a deep price-analysis run for a part.
 *
 * @param {object} args
 * @param {object} args.apiClient  the injected `useAPIClient()` client.
 * @param {string|number} args.sellerId  the active seller id (URL-scoped).
 * @param {string} args.partType   PartType LABEL string (e.g. "Headlight").
 * @param {string} args.ymm        "YYYY Make Model" (e.g. "2015 Toyota Camry").
 * @param {string} args.condition  PartCondition value (e.g. "used_good").
 * @returns {Promise<{row: object|null, locked: boolean}>} the mapped row on
 *   200/202 (`locked: false`); on a 403 whose body has `code === "upgrade_required"`
 *   returns `{ row: null, locked: true }` — any other error is rethrown.
 */
async function startDeepAnalysis({
  apiClient, sellerId, partType, ymm, condition,
}) {
  try {
    const data = await apiClient.request({
      url: `/pricing/${sellerId}/deep-analysis/`,
      method: 'POST',
      payload: { part_type: partType, ymm, condition },
    });
    return { row: mapDeepAnalysis(data), locked: false };
  }
  catch (error) {
    // The plan-locked case is a normal branch of this feature (the upsell), not
    // an error — surface it as `locked` so the caller renders the upgrade prompt.
    const response = error?.response;
    if (response?.status === 403 && response?.data?.code === 'upgrade_required') {
      return { row: null, locked: true };
    }
    throw error;
  }
}


/**
 * Fetch the current state of a deep-analysis run (poll target).
 *
 * @param {object} args
 * @param {object} args.apiClient  the injected `useAPIClient()` client.
 * @param {string|number} args.sellerId  the active seller id (URL-scoped).
 * @param {string|number} args.resultId  the row id returned by `startDeepAnalysis`.
 * @param {string} [args.askingPrice]  optional asking price — appended as the
 *   `asking_price` query param ONLY when a non-empty value is given, so the
 *   backend fills `verdict` (band placement) for it.
 * @param {AbortSignal} [args.signal]  passed through so TanStack Query can cancel.
 * @returns {Promise<object|null>} the mapped row.
 */
async function getDeepAnalysis({
  apiClient, sellerId, resultId, askingPrice, signal,
}) {
  // Only send asking_price when it's a real value — an empty string would make
  // the backend attempt a verdict on nothing.
  const payload = {};
  if (askingPrice !== undefined && askingPrice !== null && askingPrice !== '') {
    payload.asking_price = askingPrice;
  }

  const data = await apiClient.request({
    url: `/pricing/${sellerId}/deep-analysis/${resultId}/`,
    method: 'GET',
    payload,
    options: signal ? { signal } : {},
  });

  return mapDeepAnalysis(data);
}


export { getDeepAnalysis, mapDeepAnalysis, startDeepAnalysis };
